<template>
    <div :class="[item.isGroup ? 'multi-filter-group b-b sm-b-xs' : '']">
        <div class="multi-filter-item f fa-c"
            :key="item.value + item.uid"
            :class="[
                isSelected && !engine.isMultiMode ? 'el--active' : '',
                canSingleSelect || canClickRowForSelect ? 'c-p' : '',
                isResticted ? 'restricted' : '',
                showHover ? 'show-hover' : '',
                isLastChild ? '' : 'sm-b-xs'
            ]"
            :title="uiTooltip"
            @click="onItemSelected">
            <FormCheckbox v-if="showCheckmark"
                size="s"
                display="check"
                v-model="checked"
                :intermediate="isPartialSelected && !engine.isolateSelection"
                :disabled="!canCheck"
                @input="onCheckChanged" />
            <div class="f fa-l fv el-title"
                :class="[
                    showCheckmark ? 'sm-l-m' : '',
                    item.isGroup ? 'te-fat-section' : '',
                ]">
                <div v-if="qstring.length > 0" class="te-subnote el-breadcrumbs">{{ breadcrumbs }}</div>
                <div class="fg">{{ item.label | athT }}</div>
                <TruncatedText class="te-subnote dw-10" :uiText="item.subtitle" />
            </div>
            <div class="fg"></div>
            <!-- Custom badge provided by consumer -->
            <VisualBadge v-if="item.badge.length > 0" :lbl="item.badge" :cname="item.badge_style" />
            <!-- Automated badge counts -->
            <VisualBadge v-if="showBadgeCount" :lbl="badgeCount" class="sm-l-s" />
            <ButtonIcon v-if="item.isGroup && !canClickRowForSelect"
                class="el--transparent el--s-s sm-l-s"
                :icon="collapsed ? 'chevron-down' : 'chevron-up'"
                @click.prevent.stop="onToggleChildren" />
            <VisualIcon v-if="item.isGroup && canClickRowForSelect"
                class="sm-l-s"
                :name="collapsed ? 'chevron-down' : 'chevron-up'" />
        </div>
        <!-- Quick pills to remove items -->
        <div v-if="item.isGroup && collapsed && showPillsWhenCollapsed && selectedChildren.length > 0"
            class="sp-l-s sp-r-s sp-b-m sp-t-xs f">
            <InfoPill v-for="child in selectedChildren"
                :uiLabel="child.label"
                :key="child.value + child.uid"
                @onRemove="onRemoveChild(child)" />
        </div>
        <div v-if="item.isGroup"
            class="multi-filter-item-children"
            :class="[collapsed ? 'hide-children' : 'show-children sp-b-xs']">
            <multi-filter-item v-for="(child, index) in uiItems"
                :item="child"
                :key="child.value + child.uid"
                :qstring="qstring"
                :showBadgeCounts="showBadgeCounts"
                :engine="engine"
                :isLastChild="index === uiItems.length - 1"
                :startCollapsed="startCollapsed"
                :showPillsWhenCollapsed="showPillsWhenCollapsed"
                @onItemSelected="onChildItemSelected"
                @onCheck="onChildCheck" />
        </div>
    </div>
</template>
<script>
import FormCheckbox from '../../atoms/forms/FormCheckbox.vue';
import ButtonIcon from '../../atoms/ButtonIcon.vue';
import VisualBadge from '../../atoms/visuals/Badge.vue';
import VisualIcon from '../../atoms/visuals/Icon.vue';
import TruncatedText from '../../atoms/visuals/TruncatedText.vue';
import InfoPill from '../../atoms/visuals/InfoPill.vue';

export default {
    name: 'multi-filter-item',
    components: {FormCheckbox, ButtonIcon, VisualBadge, VisualIcon, TruncatedText, InfoPill},
    props: {
        /**
         * The current search query string typed into the filter's search box. When non-empty,
         * breadcrumbs are shown above the item's label and the value is passed down to child items.
         */
        qstring: {type: String, default: ''},
        /**
         * The multi-step filter item model rendered by this row, including its label, subtitle,
         * badge, sort direction, and (if a group) its children.
         */
        item: {type: Object, default: () => {}},
        /**
         * Whether to show a badge with the count of selected descendants next to a group item.
         */
        showBadgeCounts: {type: Boolean, default: true},
        /**
         * Whether a group item's children should be collapsed by default when the component is created.
         */
        startCollapsed: {type: Boolean, default: true},
        /**
         * Whether this item is the last child within its parent's list, used to control whether
         * bottom spacing is applied between siblings.
         */
        isLastChild: {type: Boolean, default: false},
        /**
         * Whether to render removable pills for currently selected descendants when a group item
         * is collapsed.
         */
        showPillsWhenCollapsed: {type: Boolean, default: false},
        /**
         * The filter engine instance that tracks selection state and provides helpers such as
         * isSelected, isPartiallySelected, and isRestrictedItem.
         */
        engine: {type: Object, default: () => {}},
    },
    data() {
        return {
            checked: this.item.calculateCheckedState(this.engine),
            collapsed: this.startCollapsed,
            selectedChildren: [],
        };
    },
    methods: {
        onItemSelected: function (event) {
            if (this.canClickRowForSelect) {
                event.preventDefault();
                event.stopPropagation();
                if (this.item.isGroup) {
                    this.onToggleChildren();
                }
                else if (this.canCheck) {
                    this.checked = !this.checked;
                    this.onCheckChanged();
                }
                return;
            }
            if (!this.canSingleSelect) return;
            event.preventDefault();
            event.stopPropagation();
            this.$emit('onItemSelected', this.item);
        },
        onToggleChildren: function () {
            this.collapsed = !this.collapsed;
        },
        onCheckChanged: function () {
            this.$emit('onCheck', this.item.getCheckedList(this.checked, this.engine));
        },
        /**
         * Forward child selection up the chain
         * @param selection
         */
        onChildCheck: function (selection) {
            this.$emit('onCheck', selection);
        },
        /**
         * Called when a child pill is clicked to remove
         * @param child
         */
        onRemoveChild: function (child) {
            this.$emit('onCheck', child.getCheckedList(false, this.engine));
        },
        /**
         * Forward child item selection up
         * @param item
         */
        onChildItemSelected: function (item) {
            this.$emit('onItemSelected', item);
        },
        /**
         * Compute item list of all selected descendents to show pills
         */
        computeSelectedChildren: function () {
            const selectedChildren = [];
            const addChildren = (item) => {
                if (item.isGroup) {
                    for (const child of item.children) {
                        addChildren(child);
                    }
                }
                // Dont add main parent, but add all other selected items
                if (item.value !== this.item.value && this.engine.isSelected(item))
                    selectedChildren.push(item);
            }
            addChildren(this.item);
            this.selectedChildren = selectedChildren;
        },
    },
    computed: {
        uiItems: function () {
            return this.item.getSortedChildren();
        },
        uiTooltip: function () {
            return this.isResticted ? MeadCommon.I18N.go('mead.x_is_already_selected', {value: this.item.label})
                : this.item.label;
        },
        canClickRowForSelect: function () {
            return this.item.canClickRowForSelect(this.engine);
        },
        isSelected: function () {
            return this.engine.isSelected(this.item);
        },
        isPartialSelected: function () {
            return this.engine.isPartiallySelected(this.item);
        },
        isResticted: function () {
            return this.engine.isRestrictedItem(this.item);
        },
        showHover: function () {
            return this.canSingleSelect || (this.canCheck && this.showCheckmark) || this.canClickRowForSelect
        },
        canSingleSelect: function () {
            return this.item.canSingleSelect(this.engine);
        },
        showCheckmark: function () {
            return this.item.showCheckmark(this.engine);
        },
        breadcrumbs: function () {
            let label = '';
            for (const crumb of this.item.breadcrumbs) {
                if (label.length !== 0) label += ' > '
                label += MeadCommon.I18N.go(crumb.label);
            }
            return label;
        },
        /**
         * If we can check or uncheck the item
         */
        canCheck: function () {
            return this.item.canToggleCheckedState(this.engine, this.checked);
        },
        showBadgeCount: function () {
            return this.showBadgeCounts && this.engine.isMultiMode && this.item.isGroup && this.badgeCount > 0;
        },
        badgeCount: function () {
            return this.item.getBadgeCount(this.engine);
        }
    },
    watch: {
        engine: {
            deep: true,
            handler: function () {
                this.checked = this.item.calculateCheckedState(this.engine)
                this.computeSelectedChildren();
            }
        },
        startCollapsed: function () {
            this.collapsed = this.startCollapsed;
        }
    },
    created: function () {
        this.computeSelectedChildren();
    }
};
</script>
<style lang="scss" scoped>
@use '../../../../sass/variables' as *;
.multi-filter-group > .multi-filter-item {
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: var(--c-panel-bg);
}

.multi-filter-item-children {
    transition: all .2s ease;
    overflow: hidden;
    padding: $s-xs $s-s $s-m $s-s;

    &.hide-children {
        margin-top: 0;
        height: 0;
        padding: 0 $s-s;

        .multi-filter-item {
            transition: all .2s ease;
            //height: 0;
            // Only hide top and bottom to ensure collapsing looks correct
            // padding-top: 0 !important;
            // padding-bottom: 0 !important;
            // margin-top: 0 !important;
            // margin-bottom: 0 !important;
        }
    }

    &.show-children {
        height: initial;

        .multi-filter-item {
            transition: all .2s ease;
            //height: initial;
            //padding: 1rem !important;
            //margin: 0 0 .5rem 0;
        }
    }

    // Expand and collapse instantly
    @media (prefers-reduced-motion: reduce) {
        &,
        .multi-filter-item {
            transition: none !important;
        }
    }
}

.multi-filter-item {
    transition: all .2s ease;
    letter-spacing: $ls-s;
    border-radius: $br-m;
    padding: 0.8rem $s-m;
    line-height: $lh-m;
    white-space: normal;
    transition: background-color 0.5s ease;

    .el-breadcrumbs {
        text-wrap: auto;
    }

    &.restricted {
        opacity: .5;
    }

    &.show-hover {
        &:not(.el--active):active,
        &:not(.el--active):hover {
            background-color: var(--c-brand-light-bg);

            .te-subnote {
                color: var(--c-brand-light-fg);
            }
        }
    }

    &.el--active {
        background-color: var(--c-brand-default-bg);
    }

    .el-title {
        min-width: 5%;
    }
}
</style>
